/**
 * AuthContext.tsx - Customer Authentication Context
 * Handles phone-based authentication state
 */

import React, { createContext, useContext, useState, useEffect, useMemo, ReactNode } from 'react';

export const SEAT_LIFESPAN_MS = 12 * 60 * 60 * 1000;

export interface User {
  id: string;
  phoneNumber: string;
  name?: string;
  section?: string;
  row?: string;
  seat?: string;
  seatConfirmedAt?: string;
}

function isSeatComplete(user: User): boolean {
  return !!(user.section?.trim() && user.row?.trim() && user.seat?.trim());
}

export function computeSeatStale(user: User | null): boolean {
  if (!user) return false;
  if (!isSeatComplete(user)) return false;
  if (!user.seatConfirmedAt) return true;
  const stampedAt = new Date(user.seatConfirmedAt).getTime();
  if (Number.isNaN(stampedAt)) return true;
  return Date.now() - stampedAt > SEAT_LIFESPAN_MS;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  isSeatStale: boolean;
  login: (user: User) => void;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const isSeatStale = useMemo(() => computeSeatStale(user), [user]);

  // Load user from localStorage on mount
  useEffect(() => {
    const storedUser = localStorage.getItem('customer_user');
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setUser(userData);
        setIsAuthenticated(true);
      } catch (error) {
        console.error('Failed to parse stored user:', error);
        localStorage.removeItem('customer_user');
      }
    }
  }, []);

  const login = (userData: User) => {
    setUser(userData);
    setIsAuthenticated(true);
    localStorage.setItem('customer_user', JSON.stringify(userData));
  };

  const logout = () => {
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('customer_user');
    localStorage.removeItem('authToken');
  };

  const updateUser = (userData: Partial<User>) => {
    if (user) {
      const updatedUser = { ...user, ...userData };
      setUser(updatedUser);
      localStorage.setItem('customer_user', JSON.stringify(updatedUser));
    }
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, isSeatStale, login, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};
